import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill } from '@/components/live';
import { usePickers } from '@/hooks/usePickers';
import { optionLabel } from '@/lib/practiceLists';

/**
 * The chemicals a treatment plant uses on wood (owner, 2026-09-27: "plant management for chemical
 * treatment"). One list, with the WHO hazard class and the FSC highly-hazardous flag, so a tank
 * batch names a chemical from here and the hazardous check is one check. The flag follows class
 * 1a and 1b on save (migration 320000), and a highly hazardous chemical must carry the derogation
 * reference it is used under.
 */
interface ChemicalRow {
  id: string; chemical_code: string | null; product_name: string; active_ingredient: string | null;
  hazard_class: string; fsc_hhp: boolean; fsc_derogation_ref: string | null; sds_document_id: string | null;
  supplier_party_id: string | null; supplier_name: string | null; status: string; notes: string | null;
}

export const HAZARD_CLASSES = [
  { id: 'class_1a', label: 'Class Ia - extremely hazardous' }, { id: 'class_1b', label: 'Class Ib - highly hazardous' },
  { id: 'class_2', label: 'Class II - moderately hazardous' }, { id: 'class_3', label: 'Class III - slightly hazardous' },
  { id: 'unlikely', label: 'Unlikely to present hazard' }, { id: 'unclassified', label: 'Not classified' },
];

export default function TreatmentChemicalsScreen() {
  const pick = usePickers(['parties']);
  return (
    <RegisterScreen<ChemicalRow>
      addLabel="Add chemical"
      columns={[
        { key: 'product_name', label: 'Product', render: (row) => <><strong>{row.product_name}</strong><small className="muted">{row.chemical_code ?? ''}</small></> },
        { key: 'active_ingredient', label: 'Active ingredient', render: (row) => row.active_ingredient ?? '--' },
        { key: 'hazard_class', label: 'Hazard class', render: (row) => optionLabel(HAZARD_CLASSES, row.hazard_class) },
        { key: 'fsc_hhp', label: 'FSC', render: (row) => (row.fsc_hhp ? <span className="badge badge-red">FSC HHP</span> : '--') },
        { key: 'supplier_name', label: 'Supplier', render: (row) => row.supplier_name ?? '--' },
        { key: 'sds_document_id', label: 'Safety data sheet', render: (row) => (row.sds_document_id ? 'On file' : <span className="warn">None on file</span>) },
        { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
      ]}
      defaults={{ hazard_class: 'unclassified', fsc_hhp: false, status: 'active' }}
      exportName="treatment-chemicals"
      fields={[
        { key: 'product_name', label: 'Product / brand name', type: 'text', required: true, placeholder: 'e.g. CCA 60% (copper chrome arsenate)' },
        { key: 'active_ingredient', label: 'Active ingredient', type: 'text' },
        { key: 'hazard_class', label: 'Hazard class (WHO)', type: 'select', options: HAZARD_CLASSES, required: true,
          hint: 'Class Ia and Ib are highly hazardous by definition; the FSC flag follows.' },
        { key: 'fsc_hhp', label: 'On the FSC highly hazardous pesticides list', type: 'check' },
        { key: 'fsc_derogation_ref', label: 'FSC derogation / risk assessment ref.', type: 'text',
          required: (form) => Boolean(form.fsc_hhp) || ['class_1a', 'class_1b'].includes(String(form.hazard_class)),
          showIf: (form) => Boolean(form.fsc_hhp) || ['class_1a', 'class_1b'].includes(String(form.hazard_class)) },
        { key: 'supplier_party_id', label: 'Supplier', type: 'select', options: pick.parties, hint: 'A registered party. Engage it as a supplier on the Party engagements page.' },
        { key: 'sds_document_id', label: 'Safety data sheet', type: 'file', accept: 'application/pdf,image/*' },
        { key: 'status', label: 'Status', type: 'select', options: [{ id: 'active', label: 'Active' }, { id: 'inactive', label: 'Inactive' }], required: true },
        { key: 'notes', label: 'Notes', type: 'textarea', wide: true },
      ]}
      recordLabel={(row) => row.product_name}
      recordNoun="chemical"
      screenKey="site.register"
      searchColumns={['product_name', 'active_ingredient', 'chemical_code', 'supplier_name']}
      stats={[
        { key: 'hhp', label: 'FSC highly hazardous', tone: 'error', match: (row) => row.fsc_hhp },
        { key: 'nosds', label: 'No safety data sheet', tone: 'warn', match: (row) => !row.sds_document_id },
        { key: 'active', label: 'Active', tone: 'ok', match: (row) => row.status === 'active' },
      ]}
      subtitle="The chemicals the treatment plant uses on wood, with their hazard class and papers."
      table="ops.treatment_chemicals"
      title="Treatment chemicals"
      view="treatment_chemicals"
    />
  );
}
